import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Center,
  Text,
  useToast
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

import LoggedInUserWrapper from '../core/LoggedInUserWrapper';
import AssignmentQuestion from './AssignmentQuestion';
import {
  useAuth,
  useFixedHeaderOffset,
  useNetworkStatus,
  usePageVisibility,
  useParams,
  useStudentAssignmentActions,
  useStudentAssignmentTimer
} from '../../hooks';
import { useGetStudentAssignment } from '../../queries';
import AssignmentAction from './AssignmentActions';
import { ASSIGNMENT_END_REASON, QUESTION_RESPONSE_STATUS, QUESTION_TYPES } from '../../constants';
import { AssignmentDrawer } from './AssignmentDrawer';
import { withAuth } from '../auth';
import { ConfirmationModal } from '../shared';
import { AssignmentProvider, useAssignmentContext } from './context/AssignmentContext';
import TestHeader from './TestHeader';
import BottomNavigation from './BottomNavigation';
import FloatingNavigationButton from './FloatingNavigationButton';
import { getRcContext, preloadMediaBlob } from '../../utils';
import { getAssignmentQuestionURL } from '../../utils/urlHelpers';
import { buildDrawerQuestionNumbers } from '../../utils/drawerRows';

const AssignmentContent = () => {
  const { studentAssignmentId, questionId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { token } = useAuth();
  const { isMediaBusy, stopActiveRecording, setMobileActionsHost } = useAssignmentContext();
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);
  const scrollableRef = useRef(null);
  const testHeaderRef = useRef(null);
  const scrollPaddingTop = useFixedHeaderOffset({
    headerRef: testHeaderRef,
    scrollRef: scrollableRef
  });

  const {
    isLoading,
    isFetching,
    data: assignmentData,
    refetch: refetchAssignment
  } = useGetStudentAssignment(studentAssignmentId);

  const {
    isSubmittingResponse,
    assignment,
    isLastQuestion,
    isFirstQuestion,
    totalQuestions,
    currentQuestion,
    currentQuestionIndex,
    currentQuestionResponse,
    isEndAssignmentLoading,
    isBookmarkLoading,
    bookmarkAction,
    onSkip,
    onSave,
    onNext,
    onPrevious,
    onEndTest,
    onSaveAndNext,
    visitQuestion
  } = useStudentAssignmentActions({ assignmentData, refetchAssignment });

  React.useEffect(() => {
    if (
      currentQuestionResponse?.status &&
      currentQuestionResponse.status === QUESTION_RESPONSE_STATUS.NOT_VISITED
    ) {
      // visitQuestion(currentQuestionResponse);
    }
  }, [currentQuestionResponse, visitQuestion]);

  useEffect(() => {
    const resetScroll = () => {
      window.scrollTo({ top: 0, behavior: 'instant' });
      scrollableRef.current?.scrollTo({ top: 0, behavior: 'instant' });
    };

    resetScroll();
    const timeoutId = setTimeout(resetScroll, 50);
    return () => clearTimeout(timeoutId);
  }, [questionId]);

  // Keep current ±1 audio/video recordings warm in the session cache.
  useEffect(() => {
    if (!token || !assignmentData?.studentResponse?.length) return;

    const questionTypeById = new Map(
      (assignmentData.assignment?.questions || []).map((q) => [q.id, q.type])
    );
    const responses = assignmentData.studentResponse;
    const indices = [currentQuestionIndex - 1, currentQuestionIndex, currentQuestionIndex + 1];

    indices.forEach((index) => {
      const response = responses[index];
      if (!response?.uploadedFile?.fileKey) return;

      const type = questionTypeById.get(response.questionId);
      if (type !== QUESTION_TYPES.AUDIO && type !== QUESTION_TYPES.VIDEO) return;

      preloadMediaBlob({
        fileKey: response.uploadedFile.fileKey,
        token,
        questionId: response.questionId
      });
    });
  }, [token, assignmentData, currentQuestionIndex]);

  const handleEndTest = React.useCallback(
    async (reason) => {
      if (isMediaBusy) {
        toast({
          title: "Time's up!",
          description: 'Saving your recording before ending...',
          status: 'warning',
          duration: 4000,
          isClosable: true
        });
        await stopActiveRecording();
      }
      onEndTest(reason);
    },
    [isMediaBusy, stopActiveRecording, onEndTest, toast]
  );

  const handleFinalAction = React.useCallback(
    (eventType) => {
      let endReason = ASSIGNMENT_END_REASON.STRICT_MODE_TAB_VIOLATION;
      if (eventType === 'alert-timer-expired') {
        endReason = ASSIGNMENT_END_REASON.STRICT_MODE_TIME_VIOLATION;
      }
      handleEndTest(endReason);
    },
    [handleEndTest]
  );

  const {
    isWarningModalOpen,
    onWarningModalClose,
    warningModalData,
    pause: pauseStrictMode,
    resume: resumeStrictMode
  } = usePageVisibility({
    enableWarningSystem: assignment?.settings?.enableStrictMode,
    maxWarnings: 3,
    alertTimer: 15,
    onFinalAction: handleFinalAction
  });

  const { countdown, pause, start } = useStudentAssignmentTimer({
    enabled: !isLoading && assignmentData?.duration?.total,
    onEndTest: handleEndTest,
    timeRemaining: assignmentData?.duration?.remaining,
    studentAssignmentId
  });

  const { isOnline } = useNetworkStatus();
  const isInteractionLocked = !isOnline;
  const mediaPausedTimerRef = useRef(false);

  // Pause the assignment timer while audio/video is recording or uploading.
  useEffect(() => {
    if (!assignmentData?.duration?.total) return undefined;

    if (isMediaBusy) {
      pause();
      mediaPausedTimerRef.current = true;
      return undefined;
    }

    if (mediaPausedTimerRef.current && isOnline) {
      mediaPausedTimerRef.current = false;
      start();
    }

    return undefined;
  }, [isMediaBusy, isOnline, pause, start, assignmentData?.duration?.total]);

  useEffect(() => {
    if (isInteractionLocked) {
      setIsNavigationOpen(false);
    }
  }, [isInteractionLocked]);

  const rcContext = getRcContext(questionId, assignmentData);

  // The student's numbers - 2b, 3.2a - in the order their paper was dealt, so the header calls
  // a question what the tray calls it.
  const questionNumbers = React.useMemo(
    () =>
      buildDrawerQuestionNumbers({
        sections: assignmentData?.assignment?.sections || [],
        assignmentQuestions: assignmentData?.assignment?.questions || [],
        responses: assignmentData?.studentResponse
      }),
    [
      assignmentData?.assignment?.sections,
      assignmentData?.assignment?.questions,
      assignmentData?.studentResponse
    ]
  );
  const questionLabel = questionNumbers[String(questionId)] || '';

  const handleRcSubQuestionJump = (subQuestionId) => {
    navigate(getAssignmentQuestionURL({ studentAssignmentId, questionId: subQuestionId }));
  };

  return (
    <LoggedInUserWrapper
      testMode
      headerProps={{
        title: assignment?.name,
        headerRightRenderer: () => (
          <AssignmentAction
            showPauseButton={false}
            onEndTest={() => handleEndTest()}
            pauseTimer={pause}
            startTimer={start}
            hasTimer={!!assignmentData?.duration?.total}
            countdown={countdown}
            isSubmitting={isEndAssignmentLoading}
            isMediaBusy={isMediaBusy}
            assignment={assignmentData?.assignment}
            studentResponse={assignmentData?.studentResponse}
          />
        ),
        time: assignmentData?.duration?.total ? countdown : null
      }}
      enableStrictMode={assignmentData?.assignment?.settings?.enableStrictMode}
    >
      {!assignment && !isFetching ? (
        <Center w="100%" h="100%">
          <Alert status="error" variant="left-accent" w={300}>
            <AlertIcon />
            <Box>
              <AlertTitle>Error!</AlertTitle>
              <AlertDescription>Invalid Assignment ID</AlertDescription>
            </Box>
          </Alert>
        </Center>
      ) : (
        <>
          <div
            aria-hidden={isInteractionLocked || undefined}
            className={
              isInteractionLocked
                ? 'pointer-events-none flex min-h-0 flex-1 select-none flex-col'
                : 'flex min-h-0 flex-1 flex-col'
            }
          >
            <div ref={testHeaderRef} className="fixed top-[62px] left-0 right-0 z-40 bg-white">
              <TestHeader
                currentQuestionIndex={currentQuestionIndex}
                totalQuestions={totalQuestions}
                questionLabel={questionLabel}
                score={currentQuestion?.score}
                isBookmarked={currentQuestionResponse?.isBookmarked}
                isBookmarkLoading={isBookmarkLoading}
                onToggleBookmark={() => bookmarkAction(currentQuestionResponse)}
                rcContext={rcContext}
              />
            </div>

            <FloatingNavigationButton
              onOpenNavigation={() => setIsNavigationOpen(true)}
              disabled={isMediaBusy || isSubmittingResponse}
            />

            <div
              ref={scrollableRef}
              style={{ paddingTop: scrollPaddingTop }}
              className="assignment-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[calc(13.5rem+env(safe-area-inset-bottom,0px))] md:pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))]"
            >
              <AssignmentQuestion
                question={currentQuestion}
                questionIndex={currentQuestionIndex}
                isLastQuestion={isLastQuestion}
                questionResponse={currentQuestionResponse}
                isBookmarkLoading={isBookmarkLoading}
                onSkip={onSkip}
                onSave={onSave}
                onNext={onNext}
                isLoading={isLoading || isEndAssignmentLoading}
                isSubmittingResponse={isSubmittingResponse}
                onSaveAndNext={onSaveAndNext}
                bookmarkAction={bookmarkAction}
                pauseStrictMode={pauseStrictMode}
                isStrictModeActive={assignment?.settings?.enableStrictMode}
                resumeStrictMode={resumeStrictMode}
                rcContext={rcContext}
                assignmentData={assignmentData}
                onRcSubQuestionJump={handleRcSubQuestionJump}
              />
            </div>

            <div className="fixed bottom-0 left-0 right-0 z-40">
              <div ref={setMobileActionsHost} />
              <div className="md:hidden">
                <BottomNavigation
                  currentQuestionIndex={currentQuestionIndex}
                  totalQuestions={totalQuestions}
                  questionLabel={questionLabel}
                  onPrevious={onPrevious}
                  onNext={onNext}
                  onOpenNavigation={() => setIsNavigationOpen(true)}
                  studentResponse={assignmentData?.studentResponse}
                  rcContext={rcContext}
                  isFirstQuestion={isFirstQuestion}
                  isLastQuestion={isLastQuestion}
                  isDisabled={isMediaBusy || isSubmittingResponse}
                />
              </div>
            </div>

            <AssignmentDrawer
              isOpen={isNavigationOpen && !isInteractionLocked}
              onClose={() => setIsNavigationOpen(false)}
              onEndTest={() => handleEndTest()}
              isEndAssignmentLoading={isEndAssignmentLoading}
              assignmentQuestionSections={assignmentData?.assignment?.sections || []}
              assignmentQuestions={assignmentData?.assignment?.questions || []}
              studentResponse={assignmentData?.studentResponse}
            />
          </div>

          {assignment?.settings?.enableStrictMode && (
            <ConfirmationModal
              isOpen={isWarningModalOpen}
              onClose={onWarningModalClose}
              headerText={`⚠️ Warning ${warningModalData.currentWarning} of ${warningModalData.maxWarnings}`}
              modalText={
                <Box>
                  <Text>
                    You seem to have switched screens. Your assignment will be auto-submitted if you
                    don&#39;t come back soon.
                  </Text>
                  <Text mt={3} fontWeight="bold">
                    Click &quot;I understand&quot; or your assignment will be auto submitted in 15
                    seconds.
                  </Text>
                </Box>
              }
              primaryButtonText={`I understand (${warningModalData.timeRemaining}s)`}
              onAction={onWarningModalClose}
              isClosable={false}
            />
          )}
        </>
      )}
    </LoggedInUserWrapper>
  );
};

const Assignment = () => (
  <AssignmentProvider>
    <AssignmentContent />
  </AssignmentProvider>
);

const AssignmentWithAuth = withAuth(Assignment);
export default AssignmentWithAuth;
